import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects'
import { useFunding } from '../../hooks/useFunding'
import RoleBadge from '../layout/RoleBadge'
import { ProjectsByStatusPie, BudgetUtilizationBar } from '../common/DashboardCharts.jsx'
import AIActionGuide from '../ai/AIActionGuide.jsx'
import { useTranslation } from 'react-i18next';

function FunderDashboard() {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects } = useProjects() // real projects
  const { fundingRequests, approveFundingRequest, loading: fundingLoading } = useFunding() // real funding requests

  const money = (n) => n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${(n || 0).toLocaleString()}`

  // Calculate Stats — committed (approved) vs disbursed (spent) vs remaining
  const { fundingStats, committed, disbursed } = useMemo(() => {
    const list = Array.isArray(projects) ? projects : []
    const committed = list.reduce((sum, p) => sum + (p.approvedBudget || 0), 0);   // approved/funded
    const disbursed = list.reduce((sum, p) => sum + (p.spentBudget || 0), 0);      // actually paid out
    const activeCount = list.filter(p => p.status === 'In Progress').length;
    const pendingCount = Array.isArray(fundingRequests) ? fundingRequests.filter(r => r.status === 'Pending').length : 0;

    return {
      committed, disbursed,
      fundingStats: [
        { name: t('committed_approved'), value: money(committed), icon: '💰' },
        { name: t('disbursed_paid'), value: money(disbursed), icon: '💸' },
        { name: t('active_investments'), value: activeCount, icon: '📈' },
        { name: t('pending_approvals'), value: pendingCount, icon: '⏳' },
      ],
    }
  }, [projects, fundingRequests]);

  // Available funds = the funder's authorised budget minus what's committed.
  const authority = user?.total_budget_authority || 0
  const available = authority > 0 ? Math.max(0, authority - committed) : null

  const portfolioProjects = useMemo(() => {
    if (!projects) return [];
    // Filter projects where user is Funder. logic in useProjects might already do this?
    // useProjects by default filters for the logged in user based on role logic.
    // If Funder, projects list IS the portfolio.
    // But verify logic in filteredProjects: 
    // "if (user.role === userRoles.Funder) return mapped.filter(p => p.funderId === user.id);"
    // So yes, `projects` is already the portfolio.
    return projects;
  }, [projects]);

  const pendingRequests = useMemo(() => {
    // Filter pending requests.
    if (!Array.isArray(fundingRequests)) return [];
    return fundingRequests.filter(r => r.status === 'Pending');
  }, [fundingRequests]);

  const handleApprove = async (requestId, amount) => {
    if (confirm("Approve this funding request?")) {
      await approveFundingRequest(requestId, { decision: 'approve', approvedAmount: amount });
    }
  };

  const handleReject = async (requestId) => {
    if (confirm("Reject this funding request?")) {
      await approveFundingRequest(requestId, { decision: 'reject' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* AI Copilot action guide */}
      <AIActionGuide />
      {/* header */}
      <div className="bg-linear-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('funder_title')}</h1>
            <p className="mt-1 opacity-90">{t('funder_subtitle')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{authority > 0 ? t('available_funds') : t('disbursed_to_date')}</p>
            <p className="text-2xl font-bold">{available !== null ? money(available) : money(disbursed)}</p>
            {authority > 0 && <p className="text-[11px] opacity-80">of {money(authority)} authorised</p>}
            <RoleBadge role={user?.role} />
          </div>
        </div>
      </div>

      {/* Funding Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {fundingStats.map((stat, index) => (
          <div key={index} className="card p-6 hover-lift">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className="text-2xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Dashboard Charts (Phase C2) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProjectsByStatusPie projects={projects} />
        <BudgetUtilizationBar projects={projects} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investment Portfolio */}
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('investment_portfolio')}</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {portfolioProjects.map((project, index) => (
                <div
                  key={index}
                  onClick={() => project.id && navigate(`/projects/${project.id}`)}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-blue-300"
                  title={t('open_project')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900 hover:text-blue-600">{project.name}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${project.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                      {project.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">{t('investment')}</p>
                      <p className="font-semibold">${(project.approvedBudget || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">{t('progress')}</p>
                      <p className="font-semibold">{project.progress || 0}%</p>
                    </div>
                  </div>
                  <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${project.progress || 0}%` }}
                    ></div>
                  </div>
                </div>
              ))}
              {portfolioProjects.length === 0 && <p className="text-gray-500">{t('no_investments')}</p>}
            </div>
          </div>
        </div>

        {/* Funding Requests */}
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('pending_funding_requests')}</h3>
          </div>
          <div className="p-6">
            {fundingLoading ? <p>{t('loading_requests')}</p> : (
              <div className="space-y-4">
                {pendingRequests.map((request, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">Project ID: {request.projectId}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${request.priority === 'High' ? 'bg-red-100 text-red-800' :
                        request.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                        {request.priority || 'Medium'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{request.justification || "No justification provided"}</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">{t('amount')}</p>
                        <p className="font-semibold">${(request.requestedAmount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">{t('expected_roi')}</p>
                        <p className="font-semibold">{request.expectedROI || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleApprove(request.id, request.requestedAmount)}
                        className="btn-success text-sm flex-1 sm:flex-none justify-center"
                      >
                        Approve
                      </button>
                      <button className="btn-secondary text-sm flex-1 sm:flex-none justify-center">{t('review')}</button>
                      <button
                        onClick={() => handleReject(request.id)}
                        className="btn-danger text-sm flex-1 sm:flex-none justify-center"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
                {pendingRequests.length === 0 && <p className="text-gray-500">{t('no_pending_requests')}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FunderDashboard
